import { create } from 'zustand';
import apiClient from '../api/apiClient';

export interface Location {
    id: number;
    name: string;
    status: boolean;
    type?: 'Office' | 'Training';
    address?: string;
    state?: string;
    country?: string;
    zipcode?: string;
}

interface LocationState {
    locations: Location[];
    isLoading: boolean;
    error: string | null;
    hasFetchedAll: boolean;

    fetchLocations: (opts?: { force?: boolean }) => Promise<Location[]>;
    fetchActiveLocations: (opts?: { force?: boolean }) => Promise<Location[]>;
    reset: () => void;
}

const sortLocations = (locations: Location[]): Location[] =>
    [...locations].sort((a, b) => {
        // Sort by status (active first), then by name
        if (a.status !== b.status) {
            return a.status ? -1 : 1;
        }
        return a.name.localeCompare(b.name);
    });

export const useLocationStore = create<LocationState>((set, get) => ({
    locations: [],
    isLoading: false,
    error: null,
    hasFetchedAll: false,

    async fetchLocations(opts) {
        const force = opts?.force ?? false;
        if (!force && get().hasFetchedAll) {
            if (get().isLoading) {
                set({ isLoading: false });
            }
            return get().locations;
        }

        set({ isLoading: true, error: null });

        try {
            const response = await apiClient.get('/admin/location/');
            const locationData = response.data?.data || [];
            const sorted = sortLocations(Array.isArray(locationData) ? locationData : []);

            set({
                locations: sorted,
                isLoading: false,
                error: null,
                hasFetchedAll: true,
            });

            return sorted;
        } catch (error) {
            console.error('Failed to fetch locations', error);
            const message = error instanceof Error ? error.message : 'Unable to load locations';
            set({ error: message, isLoading: false });
            throw error;
        }
    },

    async fetchActiveLocations(opts) {
        set({ isLoading: true, error: null });

        try {
            const response = await apiClient.get('/admin/location/');
            const locationData = response.data?.data || [];
            // Filter for active locations
            const activeLocations = (Array.isArray(locationData) ? locationData : []).filter(loc => loc.status === true);
            const sorted = sortLocations(activeLocations);

            set({
                locations: sorted,
                isLoading: false,
                error: null,
            });

            return sorted;
        } catch (error) {
            console.error('Failed to fetch active locations', error);
            const message = error instanceof Error ? error.message : 'Unable to load active locations';
            set({ error: message, isLoading: false });
            throw error;
        }
    },

    reset() {
        set({
            locations: [],
            isLoading: false,
            error: null,
            hasFetchedAll: false,
        });
    },
}));
